import { useLocation, useNavigate } from 'react-router-dom';

import Button from '../components/Button';
import Text from '../components/Text';
import { useEcrans } from '../config/useEcrans';
import { t } from '../i18n/langue';

import FinDeCourse from '../screens/FinDeCourse';

/**
 * La garde de route du mobile — décision #17 §6.
 *
 * **Même test, autre liste.** On ne réutilise pas `RequirePermission` : il lit
 * `config/routePermissions.ts` et se replie sur `/settings`, deux choses qui
 * n'existent pas ici — et le repli serait une impasse, les réglages n'étant pas
 * portés. Ce n'est pas une duplication du *test* (`hasPermission` et les flags
 * d'établissement restent ceux du web, via `useEcrans`), c'est la même règle
 * appliquée à la liste du registre.
 *
 * **Jamais de redirection silencieuse.** Le web renvoie sans un mot vers
 * `getFirstAccessibleRoute()` (`RequirePermission.tsx:65`). Transposé au mobile,
 * on taperait une notification annonçant le message d'un client précis pour
 * atterrir sur un tableau de bord sans explication — l'utilisateur conclut au bug
 * et appelle. Donc : une phrase neutre, et un bouton qu'il choisit de presser.
 *
 * **Le message ne nomme ni le module ni la permission manquante** (#17 §4) : le
 * dire apprendrait à un agent d'entretien la configuration de l'hôtel et
 * l'existence de ce qu'il n'a pas le droit de voir.
 */
interface GardeEcranProps {
    children?: React.ReactNode;
}

export default function GardeEcran({ children }: GardeEcranProps) {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { estEligible, atterrissage } = useEcrans();

    if (estEligible(pathname)) return <>{children}</>;

    // Aucun écran éligible du tout : le bouton n'aurait nulle part où mener, et
    // la fin de course dit exactement ça — avec sa déconnexion et son sélecteur.
    if (!atterrissage) return <FinDeCourse />;

    return (
        <div className="tf-shell h-full overflow-y-auto bg-main-bg-color">
            <main className="mx-auto flex max-w-md flex-col gap-5 px-5 py-10">
                <Text variante="titre" as="h1">
                    {t('coque.elementIndisponible')}
                </Text>
                <Text variante="corps" ton="attenue" className="leading-relaxed">
                    {t('coque.elementInaccessible')}
                </Text>

                <Button
                    libelle={t('coque.allerA', { ecran: atterrissage.libelle })}
                    onPress={() => navigate(atterrissage.chemin, { replace: true })}
                    pleineLargeur
                />
            </main>
        </div>
    );
}
